import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import {
  Section, Panel, DataTable, ProgressValue, UtilisationValue, HiddenChartTable, OnboardingBadge, StatRow, EmptyState,
  overAssignedRowStyle,
} from './shared';
import { formatCount, formatDate, formatDecimal, formatPercent } from '../utils';
import { useInsightsStyles } from '../styles';
import { useColorMode } from '../../../../theme/ColorModeProvider';
import { getChartPalette } from '../../../../theme/colorMode';

const ONBOARDING_COLORS = { completed: '#16a34a', in_progress: '#3D7DDD', not_started: '#f59e0b', no_record: '#94a3b8' };
const FALLBACK_COLORS = ['#16a34a', '#3D7DDD', '#f59e0b', '#94a3b8'];

const OnboardingFunnel = ({ funnel = {} }) => {
  const classes = useInsightsStyles();
  const steps = funnel.steps || [];
  const columns = [
    { key: 'label', label: 'Step', render: row => <strong>{row.label}</strong> },
    { key: 'done', label: 'Done', render: row => `${formatCount(row.done)} of ${formatCount(funnel.tracked)}` },
    { key: 'in_progress', label: 'In progress', align: 'right', render: row => formatCount(row.in_progress) },
    { key: 'waiting_here_now', label: 'Waiting here now', align: 'right', render: row => formatCount(row.waiting_here_now) },
    {
      key: 'stuck_here',
      label: 'Stuck here',
      align: 'right',
      render: row => <span style={Number(row.stuck_here) > 0 ? { color: '#b45309', fontWeight: 700 } : undefined}>{formatCount(row.stuck_here)}</span>,
    },
    { key: 'pct_of_tracked', label: '% of tracked', render: row => <ProgressValue value={row.pct_of_tracked} /> },
  ];
  return (
    <>
      <Typography component="h3" className={classes.subheading}>Onboarding by step</Typography>
      <div style={{ marginBottom: 12 }}>
        <StatRow items={[
          { key: 'tracked', label: 'tracked', value: funnel.tracked },
          { key: 'completed', label: 'completed', value: funnel.completed },
          { key: 'not_tracked', label: 'not tracked', value: funnel.not_tracked },
        ]} />
      </div>
      <DataTable
        ariaLabel="Customer onboarding by step"
        columns={columns}
        rows={steps}
        rowStyle={row => (Number(row.stuck_here) > 0 ? { background: 'rgba(245,158,11,.07)' } : undefined)}
        emptyText={funnel.description}
      />
      {funnel.median_days_to_complete !== null && funnel.median_days_to_complete !== undefined
        ? <Typography className={classes.footnote}>Median days to complete onboarding: <strong>{formatDecimal(funnel.median_days_to_complete)}</strong></Typography>
        : null}
      {funnel.description ? <Typography className={classes.footnote}>{funnel.description}</Typography> : null}
    </>
  );
};

/** `customers` section. */
const CustomerInsights = ({ data = {}, onCustomer }) => {
  const classes = useInsightsStyles();
  const chart = getChartPalette(useColorMode().mode);
  const [sort, setSort] = React.useState(null);
  const rows = React.useMemo(() => {
    const top = data.top || [];
    if (!sort) return top;
    return [...top].sort((a, b) => {
      const left = a[sort.key] ?? '';
      const right = b[sort.key] ?? '';
      const result = typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right));
      return sort.direction === 'asc' ? result : -result;
    });
  }, [data.top, sort]);
  const sortable = (label, key) => (
    <TableSortLabel
      active={sort?.key === key}
      direction={sort?.key === key ? sort.direction : 'asc'}
      onClick={() => setSort(current => ({ key, direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc' }))}
    >{label}</TableSortLabel>
  );
  const onboarding = data.by_onboarding || [];
  const onboardingLabels = Object.fromEntries(onboarding.map(item => [item.key, item.label]));
  const onboardingTotal = onboarding.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
  const colorFor = (item, index) => ONBOARDING_COLORS[item.key] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  const topColumns = [
    { key: 'name', label: sortable('Customer', 'name'), render: row => <><div style={{ fontWeight: 600 }}>{row.name}</div>{row.partner_label ? <div className={classes.muted} style={{ fontSize: '.72rem' }}>{row.partner_label}</div> : null}</> },
    { key: 'projects', label: sortable('Projects', 'projects'), align: 'right', render: row => formatCount(row.projects) },
    { key: 'learners', label: sortable('Learners', 'learners'), align: 'right', render: row => formatCount(row.learners) },
    { key: 'seats_assigned', label: sortable('Seats assigned / allocated', 'seats_assigned'), align: 'right', render: row => `${formatCount(row.seats_assigned)} / ${formatCount(row.seats_allocated)}` },
    { key: 'utilisation_pct', label: sortable('Utilisation', 'utilisation_pct'), render: row => <UtilisationValue value={row.utilisation_pct} overAssigned={row.over_assigned} /> },
    { key: 'course_completions', label: sortable('Completions', 'course_completions'), align: 'right', render: row => formatCount(row.course_completions) },
    { key: 'customer_since', label: sortable('Customer since', 'customer_since'), render: row => formatDate(row.customer_since) },
  ];
  const newestColumns = [
    { key: 'name', label: 'Customer', render: row => <><div style={{ fontWeight: 600 }}>{row.name}</div>{row.email ? <div className={classes.muted} style={{ fontSize: '.72rem' }}>{row.email}</div> : null}</> },
    { key: 'partner_label', label: 'Partner type' },
    { key: 'created_at', label: 'Created', render: row => formatDate(row.created_at) },
    { key: 'projects', label: 'Projects', align: 'right', render: row => formatCount(row.projects) },
    { key: 'learners', label: 'Learners', align: 'right', render: row => formatCount(row.learners) },
    { key: 'onboarding', label: 'Onboarding', render: row => <OnboardingBadge status={row.onboarding} label={onboardingLabels[row.onboarding]} /> },
  ];
  return (
    <Section id="customers" title="Customers" subtitle={data.total !== undefined ? `${formatCount(data.total)} customers` : undefined} exportKey="customers">
      <Panel>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography component="h3" className={classes.sectionTitle}>Customers by partner type</Typography>
            {(data.by_partner || []).length ? <>
              <div className={classes.chart} style={{ height: Math.max(180, data.by_partner.length * 52 + 40) }} role="img" aria-label="Horizontal bar chart of customers by partner type"><ResponsiveContainer><BarChart data={data.by_partner} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 0 }}><CartesianGrid stroke={chart.grid} horizontal={false} /><XAxis type="number" stroke={chart.axis} tick={{ fontSize: 12 }} allowDecimals={false} /><YAxis type="category" dataKey="label" width={150} stroke={chart.axis} tick={{ fontSize: 12 }} interval={0} /><ChartTooltip contentStyle={{ background: chart.tooltipBg, borderColor: chart.tooltipBorder }} formatter={(value, name, props) => [`${formatCount(value)} (${formatPercent(props.payload.pct)})`, 'Customers']} /><Bar dataKey="value" name="Customers" fill="#3D7DDD" radius={[0, 5, 5, 0]} /></BarChart></ResponsiveContainer></div>
              <HiddenChartTable caption="Customers by partner type" headers={['Partner type', 'Customers', 'Percent']} rows={data.by_partner.map(item => [item.label, item.value, formatPercent(item.pct)])} />
            </> : <div style={{ marginTop: 12 }}><EmptyState /></div>}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography component="h3" className={classes.sectionTitle}>Customer onboarding</Typography>
            <div style={{ display: 'flex', height: 28, borderRadius: 8, overflow: 'hidden', marginTop: 16, background: 'var(--dm-row-alt, #f8fafc)' }} role="img" aria-label="Stacked bar of customer onboarding states">{onboarding.map((item, index) => <Tooltip key={item.key} title={`${item.label}: ${formatCount(item.value)} (${formatPercent(item.pct)})`}><div tabIndex={0} aria-label={`${item.label}: ${formatCount(item.value)}, ${formatPercent(item.pct)}`} style={{ width: `${onboardingTotal ? (Number(item.value) / onboardingTotal) * 100 : 0}%`, background: colorFor(item, index) }} /></Tooltip>)}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(max(160px, calc(50% - 4px)), 1fr))', gap: 8, marginTop: 16 }}>
              {onboarding.map((item, index) => (
                <div key={item.key} className={classes.tile} style={{ borderLeft: `4px solid ${colorFor(item, index)}`, minWidth: 0 }}>
                  <Typography className={classes.sectionSubtitle} style={{ marginTop: 0 }}>{item.label}</Typography>
                  <Typography className={classes.metricValue} style={{ fontSize: '1.375rem' }}>{formatCount(item.value)} <span className={classes.muted} style={{ fontSize: '.8rem', fontWeight: 600 }}>{formatPercent(item.pct)}</span></Typography>
                </div>
              ))}
            </div>
            <HiddenChartTable caption="Customers by onboarding state" headers={['State', 'Customers', 'Percent']} rows={onboarding.map(item => [item.label, item.value, formatPercent(item.pct)])} />
          </Grid>
        </Grid>
        <Typography component="h3" className={classes.subheading}>Newest customers</Typography>
        <DataTable ariaLabel="Newest customers" columns={newestColumns} rows={data.newest || []} onRowClick={onCustomer} getRowKey={row => row.customer_id} />
        <OnboardingFunnel funnel={data.onboarding_funnel || {}} />
        <Typography component="h3" className={classes.subheading}>Top customers</Typography>
        <DataTable ariaLabel="Top customers" columns={topColumns} rows={rows} onRowClick={onCustomer} rowStyle={overAssignedRowStyle} getRowKey={row => row.customer_id} />
      </Panel>
    </Section>
  );
};

export default CustomerInsights;
